import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { preloadQuery } from "@relay";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { useState, useEffect } from "react";
import z from "zod";
import { toast } from "sonner";
import { Package, Clock, CheckCircle, XCircle } from "lucide-react";
import { applyTheme } from "@utils/themes";
import { useI18nContext } from "@i18n/i18n-react";

import { HubScannerDrawer } from "~/components/HubScannerDrawer";
import { OpenDoorPage } from "../$hubMetaName/pick-up/-OpenDoorPage";
import { PageContainer } from "~/components/layout/PageContainer";

import type { clickncollectQuery } from "./__generated__/clickncollectQuery.graphql";
import type { clickncollectCollectMutation } from "./__generated__/clickncollectCollectMutation.graphql";

export const query = graphql`
	query clickncollectQuery($pickupId: UUID!) {
		pickup(id: $pickupId) @required(action: THROW) {
			id
			status
			orderItems {
				id
				order {
					id
					source
				}
			}
			hub @required(action: THROW) {
				id
				meta {
					id
					name
				}
				spaces {
					id
					frame
				}
			}
			customer @required(action: THROW) {
				id
				email
				phoneNumber
				user {
					id
					name
				}
			}
			reservation @required(action: THROW) {
				id
				space {
					id
					frame
				}
			}
		}
	}
`;

const collectMutation = graphql`
	mutation clickncollectCollectMutation($pickupId: UUID!) {
		pickupCollect(input: { pickupId: $pickupId })
	}
`;

export const Route = createFileRoute("/clickncollect/")({
	beforeLoad: () => {
		document.title = "bacehub | Startseite";
	},
	component: RouteComponent,
	validateSearch: zodValidator(
		z
			.object({
				pickupId: z.string().uuid().optional(),
				pickupid: z.string().uuid().optional(),
			})
			.refine((value) => Boolean(value.pickupId || value.pickupid), {
				message: "pickupId is required",
				path: ["pickupId"],
			})
			.transform((value) => ({
				pickupId: value.pickupId ?? value.pickupid!,
			}))
	),
	loaderDeps: ({ search: { pickupId } }) => ({ pickupId }),
	loader: async ({ context, deps }) => {
		return preloadQuery<clickncollectQuery>(context.relayEnvironment, query, { pickupId: deps.pickupId });
	},
});

function RouteComponent() {
	const { pickup } = usePreloadedQuery<clickncollectQuery>(query, Route.useLoaderData());
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	useEffect(() => {
		const url = new URL(window.location.href);
		const pickupId = url.searchParams.get("pickupId") ?? url.searchParams.get("pickupid");

		if (!pickupId) return;

		const hasLegacyParam = url.searchParams.has("pickupid");
		const hasCanonicalParam = url.searchParams.has("pickupId");

		if (!hasLegacyParam && hasCanonicalParam) return;

		url.searchParams.delete("pickupid");
		url.searchParams.set("pickupId", pickupId);
		window.history.replaceState({}, "", url.toString());
	}, []);

	// Check if this is a Shop N Collect pickup (not regular Click & Collect)
	const isSncPickup = pickup.orderItems.some((item) => item.order.source === "SHOP_N_COLLECT");

	// Check if this is a Thalia pickup
	const isThaliaPickup = pickup.orderItems.some((item) => item.order.source === "THALIA");

	// Only Thalia pickups use thalia theme, everything else uses default
	useEffect(() => {
		applyTheme(isThaliaPickup ? "thalia" : "default");
	}, [isThaliaPickup]);

	// Open drawer automatically on mount (only if status is CONFIRMED)
	useEffect(() => {
		if (pickup.status === "CONFIRMED") {
			setIsDrawerOpen(true);
		}
	}, [pickup.status]);

	const handleHubConnected = (hub: { id: string; name: string; side?: "A" | "B" }) => {
		if (hub.id === pickup.hub.id) {
			setIsDrawerOpen(false);
		}
	};

	// Show status-specific pages for non-collectable statuses
	if (pickup.status === "COLLECTED") {
		return <PickupAlreadyCollectedPage />;
	}

	if (pickup.status === "EXPIRED" && !isThaliaPickup) {
		return <PickupExpiredPage />;
	}

	if (pickup.status === "BLOCKED") {
		return <PickupBlockedPage />;
	}

	if (pickup.status === "PENDING") {
		return <PickupPendingPage />;
	}

	if (pickup.status === "COLLECTING") {
		return <PickupCollectingPage />;
	}

	// Only CONFIRMED status can proceed to collect
	// Determine if hub is one-side
	// Check if all spaces have only "A" frames or only "B" frames
	const allFrames = pickup.hub.spaces.map((space) => space.frame);
	const hasA = allFrames.some((frame) => frame.startsWith("A"));
	const hasB = allFrames.some((frame) => frame.startsWith("B"));
	const isOneSideHub = hasA !== hasB; // one-side if only A or only B, not both

	// Get the space side from the reservation
	const space = pickup.reservation.space;
	const spaceSide = space.frame.at(0);

	// If hub is one-side, don't require side specification
	const side = isOneSideHub ? undefined : spaceSide;

	return (
		<>
			<ClickCollectOpenDoorPage
				pickupId={pickup.id}
				hubId={pickup.hub.id}
				hubName={pickup.hub.meta?.name || "Unknown Hub"}
				side={side}
				isSncPickup={isSncPickup}
				isThaliaPickup={isThaliaPickup}
			/>
			<HubScannerDrawer
				isOpen={isDrawerOpen}
				onClose={() => setIsDrawerOpen(false)}
				expectedHubId={pickup.hub.id}
				onHubConnected={handleHubConnected}
			/>
		</>
	);
}

const PickupAlreadyCollectedPage = () => {
	return (
		<PageContainer className="px-6">
			<div className="flex flex-col items-center justify-center min-h-[60vh] text-center gap-4">
				<CheckCircle className="size-16 text-green-500" />
				<h1 className="text-2xl font-bold">Bereits abgeholt</h1>
				<p className="text-muted-foreground">Diese Bestellung wurde bereits abgeholt.</p>
			</div>
		</PageContainer>
	);
};

const PickupExpiredPage = () => {
	return (
		<PageContainer className="px-6">
			<div className="flex flex-col items-center justify-center min-h-[60vh] text-center gap-4">
				<Clock className="size-16 text-orange-500" />
				<h1 className="text-2xl font-bold">Abholzeit abgelaufen</h1>
				<p className="text-muted-foreground">
					Die Abholzeit für diese Bestellung ist leider abgelaufen. Bitte kontaktieren Sie den Kundenservice.
				</p>
			</div>
		</PageContainer>
	);
};

const PickupBlockedPage = () => {
	return (
		<PageContainer className="px-6">
			<div className="flex flex-col items-center justify-center min-h-[60vh] text-center gap-4">
				<XCircle className="size-16 text-red-500" />
				<h1 className="text-2xl font-bold">Abholung gesperrt</h1>
				<p className="text-muted-foreground">
					Diese Abholung ist derzeit gesperrt. Bitte kontaktieren Sie den Kundenservice für weitere Informationen.
				</p>
			</div>
		</PageContainer>
	);
};

const PickupPendingPage = () => {
	return (
		<PageContainer className="px-6">
			<div className="flex flex-col items-center justify-center min-h-[60vh] text-center gap-4">
				<Package className="size-16 text-blue-500" />
				<h1 className="text-2xl font-bold">Bestellung wird vorbereitet</h1>
				<p className="text-muted-foreground">
					Ihre Bestellung wird noch vorbereitet. Sie erhalten eine Benachrichtigung, sobald sie zur Abholung bereit ist.
				</p>
			</div>
		</PageContainer>
	);
};

const PickupCollectingPage = () => {
	return (
		<PageContainer className="px-6">
			<div className="flex flex-col items-center justify-center min-h-[60vh] text-center gap-4">
				<Package className="size-16 text-yellow-500" />
				<h1 className="text-2xl font-bold">Abholung läuft</h1>
				<p className="text-muted-foreground">
					Die Abholung ist bereits im Gange. Bitte schließen Sie das Fach, nachdem Sie Ihre Bestellung entnommen haben.
				</p>
			</div>
		</PageContainer>
	);
};

type ClickCollectOpenDoorPageProps = {
	pickupId: string;
	hubId: string;
	hubName: string;
	side: string | undefined;
	isSncPickup: boolean;
	isThaliaPickup: boolean;
};

const ClickCollectOpenDoorPage = (props: ClickCollectOpenDoorPageProps) => {
	const { pickupId, hubId, hubName, side, isSncPickup, isThaliaPickup } = props;
	const navigate = useNavigate();
	const i18n = useI18nContext().LL["new-ui"];
	const [collectPickup, isCollecting] = useAsyncMutation<clickncollectCollectMutation>(collectMutation);

	return (
		<OpenDoorPage
			hubId={hubId}
			hubName={hubName}
			side={side}
			isSncPickup={isSncPickup}
			isThaliaPickup={isThaliaPickup}
			skipHubCheck={true}
			isLoading={isCollecting}
			onOpenDoor={async () => {
				if (isCollecting) {
					return;
				}

				try {
					const response = await collectPickup({ variables: { pickupId } });
					navigate({
						to: "/clickncollect/opening",
						search: {
							pickupId,
							unlockRequestId: response.pickupCollect,
							...(isSncPickup ? { snc: true } : {}),
							...(isThaliaPickup ? { thalia: true } : {}),
						},
					});
				} catch (error) {
					console.error(error);
					setTimeout(() => {
						if (window.location.pathname.includes("/clickncollect")) {
							toast.error(i18n.OPEN_DOOR_FAILED());
						}
					}, 3000);
				}
			}}
		/>
	);
};
